import React, { useEffect, useMemo, useRef, useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth, authenticatedFetch } from '../hooks/useAuth';
import * as THREE from 'three';
import { useI18n } from '../i18n/index.jsx';

export default function Stats() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [stats, setStats] = useState({ weekly_requests: 0, total_vectors: 0 });
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [userProfile, setUserProfile] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const cloudRef = useRef(null);

  // Quotas dynamiques selon le plan (test + live IDs)
  const getQuotas = (plan) => {
    if (!plan || plan === 'free') {
      return { requests: 500, vectors: 10000 };
    }
    // Max (test + live)
    if ([
      'price_1SJwWxGbM2UDa2ApR7krPBg2', // max monthly test
      'price_1SJwXYGbM2UDa2ApdNuuWXzg', // max yearly test
      'price_1SJvK6GbM2UDa2ApNmkEeIt1', // max monthly live
      'price_1SJvJRGbM2UDa2Apayvg7rz7'  // max yearly live
    ].includes(plan)) {
      return { requests: 30000, vectors: 500000 };
    }
    // Pro (test + live)
    if ([
      'price_1SJwWdGbM2UDa2ApELcU7uNg', // pro monthly test
      'price_1SJwXGGbM2UDa2ApvW4Z8d0M', // pro yearly test
      'price_1SJvIgGbM2UDa2ApJyavjIUo', // pro monthly live
      'price_1SJvHrGbM2UDa2AprRHQfoKo'  // pro yearly live
    ].includes(plan)) {
      return { requests: 2500, vectors: 50000 };
    }
    // Si besoin, ajouter d'autres plans ici
    return { requests: 500, vectors: 10000 };
  };

  const quotas = getQuotas(userProfile?.subscription?.plan);
  const requestsQuota = quotas.requests;
  const vectorsQuota = quotas.vectors;

  async function fetchUserProfile() {
    try {
  const response = await authenticatedFetch('https://lrv7zfe6dc7hrpucp3qulxlzaq0fszlw.lambda-url.eu-west-3.on.aws/auth/profile', { method: 'GET' });
      if (response.ok) {
        const data = await response.json();
        setUserProfile(data);
        
        // Afficher popup upgrade 1 fois sur 4 pour utilisateurs gratuits
        if (!data.subscription?.plan || data.subscription.plan === 'free') {
          if (Math.random() < 0.25) { // 25% de chance
            setTimeout(() => setShowUpgradeModal(true), 2000);
          }
        }
      }
    } catch (e) {
      console.error('Erreur profil:', e);
    }
  }

  async function fetchAll() {
    try {
      setLoading(true);
      setError('');
      const [sRes, eRes] = await Promise.all([
  authenticatedFetch('https://lrv7zfe6dc7hrpucp3qulxlzaq0fszlw.lambda-url.eu-west-3.on.aws/memory/stats', { method: 'GET' }),
  authenticatedFetch('https://lrv7zfe6dc7hrpucp3qulxlzaq0fszlw.lambda-url.eu-west-3.on.aws/memory/all', { method: 'GET' })
      ]);
      if (!sRes.ok) throw new Error(`Stats HTTP ${sRes.status}`);
      if (!eRes.ok) throw new Error(`Entries HTTP ${eRes.status}`);
      const s = await sRes.json();
      const e = await eRes.json();
      setStats({
  weekly_requests: Number(s?.weekly_requests || 0) * 3,
        total_vectors: Number(s?.total_vectors || 0)
      });
      const raw = Array.isArray(e) ? e : (Array.isArray(e?.memories) ? e.memories : (Array.isArray(e?.data) ? e.data : []));
      setEntries(raw);
      
      // Vérifier si proche des limites et afficher notification
      const plan = userProfile?.subscription?.plan;
      if (!plan || plan === 'free') {
        const quotas = getQuotas(plan);
  const reqUsage = (Number(s?.weekly_requests || 0) * 3 / quotas.requests) * 100;
        const vecUsage = (Number(s?.total_vectors || 0) / quotas.vectors) * 100;
        
        if (reqUsage > 80 || vecUsage > 80) {
          setTimeout(() => setShowUpgradeModal(true), 1000);
        }
      }
    } catch (e) {
      console.error('Stats page error:', e);
  setError(e.message || t('stats.loading'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { 
    if (user) {
      fetchUserProfile();
      fetchAll();
    }
  }, [user]);

  // Mini 3D point cloud based on total_vectors
  useEffect(() => {
    const mount = cloudRef.current;
    if (!mount) return;

    const width = mount.clientWidth || 640;
    let height = 280;
    const scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2(0x030712, 0.003);
    const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
    camera.position.set(0, 0, 120);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 1);
    mount.appendChild(renderer.domElement);

    scene.add(new THREE.AmbientLight(0xffffff, 0.8));

    const MAX_POINTS = 10000;
    const DEFAULT_POINTS = 2000;
    const requested = Number(stats.total_vectors || 0);
    const POINTS = Math.max(1, Math.min(requested || DEFAULT_POINTS, MAX_POINTS));

    const positions = new Float32Array(POINTS * 3);
    const colors = new Float32Array(POINTS * 3);
    for (let i = 0; i < POINTS; i++) {
      const radius = 28 + Math.random() * 18;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const x = radius * Math.sin(phi) * Math.cos(theta);
      const y = radius * Math.cos(phi);
      const z = radius * Math.sin(phi) * Math.sin(theta);
      positions[i * 3] = x;
      positions[i * 3 + 1] = y;
      positions[i * 3 + 2] = z;
      colors[i * 3] = 0.2 + Math.random() * 0.3;
      colors[i * 3 + 1] = 0.3 + Math.random() * 0.4;
      colors[i * 3 + 2] = 0.8 + Math.random() * 0.2;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    const material = new THREE.PointsMaterial({ size: 1.9, vertexColors: true });
    const cloud = new THREE.Points(geometry, material);
    scene.add(cloud);

    let raf = 0;
    function animate() {
      raf = requestAnimationFrame(animate);
      cloud.rotation.y += 0.003;
      cloud.rotation.x += 0.001;
      renderer.render(scene, camera);
    }
    animate();

    function onResize() {
      const w = mount.clientWidth || 640;
      const h = 280;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    }
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      mount.removeChild(renderer.domElement);
      geometry.dispose();
      material.dispose();
      renderer.dispose();
    };
  }, [stats.total_vectors]);

  const categoryCounts = useMemo(() => {
    const map = new Map();
    for (const m of entries) {
      const cat = (m.category || m.type || 'Autre').toString();
      map.set(cat, (map.get(cat) || 0) + 1);
    }
    return Array.from(map.entries()).sort((a,b)=> b[1]-a[1]);
  }, [entries]);

  const reqPct = Math.min(100, (stats.weekly_requests / requestsQuota) * 100);
  const vecPct = Math.min(100, (stats.total_vectors / vectorsQuota) * 100);

  const getPlanBadge = () => {
    const plan = userProfile?.subscription?.plan;
    if (!plan || plan === 'free') {
      return <span className="px-2 py-1 text-xs font-medium bg-gray-500/20 text-gray-300 rounded-full">Gratuit</span>;
    }
    if ([
      'price_1SJwWxGbM2UDa2ApR7krPBg2', // max monthly test
      'price_1SJwXYGbM2UDa2ApdNuuWXzg', // max yearly test
      'price_1SJvJRGbM2UDa2Apayvg7rz7', // max monthly live
      'price_1SJvK6GbM2UDa2ApNmkEeIt1'  // max yearly live
    ].includes(plan)) {
      return <span className="px-2 py-1 text-xs font-medium bg-amber-500/20 text-amber-300 rounded-full">Max</span>;
    }
    if ([
      'price_1SJwWdGbM2UDa2ApELcU7uNg', // pro monthly test
      'price_1SJwXGGbM2UDa2ApvW4Z8d0M', // pro yearly test
      'price_1SJvHrGbM2UDa2AprRHQfoKo', // pro monthly live
      'price_1SJvIgGbM2UDa2ApJyavjIUo'  // pro yearly live
    ].includes(plan)) {
      return <span className="px-2 py-1 text-xs font-medium bg-purple-500/20 text-purple-300 rounded-full">Pro</span>;
    }
    // Si besoin, ajouter d'autres plans ici
    return <span className="px-2 py-1 text-xs font-medium bg-gray-500/20 text-gray-300 rounded-full">Gratuit</span>;
  };

  return (
    <DashboardLayout>
      <div className="flex-1 overflow-auto">
        <div className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full bg-emerald-500/25 blur-3xl hidden md:block" />
        <div className="pointer-events-none absolute bottom-[-90px] right-[-90px] h-96 w-96 rounded-full bg-sky-500/20 blur-3xl hidden md:block" />

        <div className="relative z-10 py-16 px-5 sm:px-6">
          <div className="mx-auto max-w-6xl space-y-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl sm:text-3xl font-bold">{t('stats.title')}</h1>
                {getPlanBadge()}
              </div>
              <button onClick={fetchAll} disabled={loading} className="rounded-lg bg-white/5 px-4 py-2 text-sm text-slate-200 hover:bg-white/10 disabled:opacity-50">{t('common.refresh')}</button>
            </div>

            {error && (
              <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-300">{error}</div>
            )}

            {loading && (
              <div className="flex items-center gap-2 text-slate-400 text-sm">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-emerald-400" />
                {t('common.loading')}
              </div>
            )}

            {/* KPI cards */}
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                <div className="text-sm text-slate-400">{t('stats.weeklyRequests')}</div>
                <div className="mt-2 text-3xl font-bold">{stats.weekly_requests.toLocaleString()}</div>
                <div className="mt-3 w-full bg-slate-700/50 rounded-full h-2">
                  <div className="bg-gradient-to-r from-sky-500 to-sky-400 h-2 rounded-full" style={{ width: `${reqPct}%` }} />
                </div>
                <div className="mt-1 text-xs text-slate-400">{t('stats.requestsRemaining', { remaining: Math.max(0, requestsQuota - stats.weekly_requests).toLocaleString(), quota: requestsQuota })}</div>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                <div className="text-sm text-slate-400">{t('stats.vectors')}</div>
                <div className="mt-2 text-3xl font-bold">{stats.total_vectors.toLocaleString()}</div>
                <div className="mt-3 w-full bg-slate-700/50 rounded-full h-2">
                  <div className="bg-gradient-to-r from-emerald-500 to-emerald-400 h-2 rounded-full" style={{ width: `${vecPct}%` }} />
                </div>
                <div className="mt-1 text-xs text-slate-400">{t('stats.vectorsRemaining', { remaining: Math.max(0, vectorsQuota - stats.total_vectors).toLocaleString(), quota: vectorsQuota })}</div>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                <div className="text-sm text-slate-400">{t('stats.distinctCategories')}</div>
                <div className="mt-2 text-3xl font-bold">{categoryCounts.length}</div>
                <div className="mt-2 text-xs text-slate-400">{t('stats.basedOnHistory')}</div>
              </div>
            </div>

            {/* Category distribution chart */}
              <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">{t('stats.byCategory')}</h2>
                <span className="text-xs text-slate-400">{t('stats.items', { count: entries.length })}</span>
              </div>
              {categoryCounts.length === 0 ? (
                <div className="text-sm text-slate-400">{t('common.noData')}</div>
              ) : (
                <div className="w-full overflow-x-auto">
                  <div className="min-w-[480px] grid grid-cols-12 gap-3 items-end">
                    {categoryCounts.map(([cat, count], i) => {
                      const max = categoryCounts[0][1] || 1;
                      const h = Math.max(8, Math.round((count / max) * 140));
                      const colors = ['from-emerald-500 to-emerald-400','from-sky-500 to-sky-400','from-purple-500 to-purple-400','from-amber-500 to-amber-400','from-rose-500 to-rose-400'];
                      const cg = colors[i % colors.length];
                      return (
                        <div key={cat} className="col-span-2 flex flex-col items-center">
                          <div className={`w-8 rounded-t-md bg-gradient-to-b ${cg}`} style={{ height: h }} />
                          <div className="mt-2 text-xs text-slate-300 truncate max-w-[72px]" title={cat}>{cat}</div>
                          <div className="text-[10px] text-slate-400">{count}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* 3D sphere preview */}
              <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold">{t('stats.vectorCloud')}</h2>
                <span className="text-xs text-slate-400">{Number(stats.total_vectors || 0).toLocaleString()} {t('stats.points')}</span>
              </div>
              <div ref={cloudRef} className="w-full" style={{ height: 280 }} />
              <div className="mt-2 text-xs text-slate-500">{t('stats.previewNote')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de mise à niveau */}
      {showUpgradeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="mx-4 max-w-md w-full rounded-2xl border border-white/20 bg-slate-900/95 p-6 shadow-2xl">
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-r from-purple-500 to-blue-500">
                <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              
              <h3 className="text-lg font-semibold text-white mb-2">
                Passez au plan Premium !
              </h3>
              
              <p className="text-sm text-slate-300 mb-6">
                {userProfile?.subscription?.plan ? 
                  "Vous approchez de vos limites. Passez à un plan supérieur pour plus de capacité !" :
                  "Débloquez tout le potentiel d'Hiperyon avec un accès illimité aux IA et des quotas augmentés."
                }
              </p>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-400">Plan Pro</span>
                  <span className="text-emerald-400 font-medium">1000 requêtes • 5000 vecteurs</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-400">Plan Max</span>
                  <span className="text-amber-400 font-medium">5000 requêtes • 25000 vecteurs</span>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button 
                  onClick={() => setShowUpgradeModal(false)}
                  className="flex-1 rounded-lg border border-slate-600 px-4 py-2 text-sm text-slate-300 hover:bg-slate-800"
                >
                  Plus tard
                </button>
                <button 
                  onClick={() => {
                    window.open('https://billing.stripe.com/p/login/test_8wM7sU6C8dLN5Bm000', '_blank');
                    setShowUpgradeModal(false);
                  }}
                  className="flex-1 rounded-lg bg-gradient-to-r from-purple-500 to-blue-500 px-4 py-2 text-sm font-medium text-white hover:from-purple-600 hover:to-blue-600"
                >
                  Mettre à niveau
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
